/**
 * Session-scoped owner of chat transport, catch-up and event reduction.
 * A port of mobile `ChatHubProvider` (minus presence/typing/read-receipts,
 * which land in WU3, and minus list/room state, which T5.3/T5.4 own).
 *
 * The session persists every `message.sent` event through `chatDb`
 * (duplicate `eventId`s are no-ops) and routes reduced messages to the
 * registered room handlers; T5.3/T5.4 plug the list and room hooks in.
 */
import { api } from '@/lib/api'
import { applyEvent, getSyncCursor, messageToRow } from './chatDb'
import { getChatDeviceId, parseChatEvent, type ChatHubClient } from './chatHub'
import { parseMessage, type ChatMessage, type MessageStatus } from './types'

/** Mobile `ChatHubProvider.heartbeatInterval`. */
export const HEARTBEAT_INTERVAL_MS = 50_000

/** Mobile typing auto-clear (`Future.delayed(3s)` after `onUserTyping`). */
export const TYPING_EXPIRY_MS = 3_000

/** Mobile `sendTyping` throttle (one indicator per second per conversation). */
export const TYPING_THROTTLE_MS = 1_000

const CATCH_UP_LIMIT = 100

/** Mobile `ChatTimerFactory`: injectable so tests drive the heartbeat deterministically. */
export type HeartbeatTimer = (intervalMs: number, callback: () => Promise<void>) => () => void

const defaultTimer: HeartbeatTimer = (intervalMs, callback) => {
  const id = setInterval(() => {
    void callback()
  }, intervalMs)
  return () => clearInterval(id)
}

/** One-shot timer (mobile `Future.delayed`); injectable for deterministic tests. */
export type OneShotTimer = (delayMs: number, callback: () => void) => () => void

const defaultOneShot: OneShotTimer = (delayMs, callback) => {
  const id = setTimeout(callback, delayMs)
  return () => clearTimeout(id)
}

export interface RoomHandlers {
  onMessage?: (message: ChatMessage) => void
  onLifecycle?: (messageId: string, status: MessageStatus) => void
  /** Typing indicator: user id while typing, null when it expires. */
  onTyping?: (userId: string | null) => void
  onRead?: (userId: string) => void
}

export interface InboundMessage {
  message: ChatMessage
  /** The sender is someone else (unread/ack logic applies). */
  recipient: boolean
  /** The conversation is currently open on screen. */
  visible: boolean
}

export interface SessionEvents {
  onError?: (error: unknown) => void
  onConnectionChange?: (connected: boolean) => void
  /** Every persisted `message.sent`, registered room or not (the list hook owns previews/unread). */
  onInbound?: (inbound: InboundMessage) => void
  onPresenceChange?: (userId: string, online: boolean) => void
}

/** Late subscription for UI layers (the singleton session outlives them). */
export type SessionSubscription = Pick<
  SessionEvents,
  'onInbound' | 'onPresenceChange' | 'onConnectionChange' | 'onError'
>

const LIFECYCLE_STATUSES: MessageStatus[] = ['sending', 'sent', 'delivered', 'read']

export class ChatSession {
  private rooms = new Map<string, RoomHandlers>()
  private joined = new Set<string>()
  private userId: string | null = null
  private queue: Promise<void> = Promise.resolve()
  private stopHeartbeatFn: (() => void) | null = null
  private connected = false
  private error: unknown = null
  private onlineUsers = new Map<string, boolean>()
  private _visibleConversationId: string | null = null
  private lastTyping = new Map<string, number>()
  private typingTimers = new Map<string, () => void>()
  private lifecycleCleanup: (() => void) | null = null
  private subscriptions = new Set<SessionSubscription>()

  constructor(
    private readonly hub: ChatHubClient,
    private readonly events: SessionEvents = {},
    private readonly timer: HeartbeatTimer = defaultTimer,
    private readonly schedule: OneShotTimer = defaultOneShot,
  ) {}

  get isConnected(): boolean {
    return this.connected
  }

  get lastError(): unknown {
    return this.error
  }

  /** Mobile `isUserOnline`: seeded from the list, kept fresh by presence events. */
  isUserOnline(userId: string): boolean {
    return this.onlineUsers.get(userId) ?? false
  }

  get visibleConversationId(): string | null {
    return this._visibleConversationId
  }

  /** Test hook mirroring mobile `eventsSettled`: resolves when the queued events drain. */
  get queueSettled(): Promise<void> {
    return this.queue
  }

  /** Late subscription for UI layers; returns an unsubscribe cleanup. */
  subscribe(subscription: SessionSubscription): () => void {
    this.subscriptions.add(subscription)
    return () => {
      this.subscriptions.delete(subscription)
    }
  }

  async start(userId: string): Promise<void> {
    if (this.userId === userId && this.connected) return
    if (this.userId != null && this.userId !== userId) await this.stop()
    this.userId = userId
    this.wire()
    await this.connect()
    await this.catchUp()
  }

  async stop(): Promise<void> {
    this.stopHeartbeat()
    this.lifecycleCleanup?.()
    this.lifecycleCleanup = null
    this.rooms.clear()
    this.joined.clear()
    this.onlineUsers.clear()
    this._visibleConversationId = null
    this.lastTyping.clear()
    for (const cancel of this.typingTimers.values()) cancel()
    this.typingTimers.clear()
    this.userId = null
    await this.disconnect()
  }

  /** Keyed room routing: the room mounts, joins its group, unmount leaves it. */
  async registerRoom(conversationId: string, handlers: RoomHandlers = {}): Promise<void> {
    this.rooms.set(conversationId, handlers)
    // Mobile marks the opened conversation read (the list hook clears its
    // own unread copy when it sees the visible inbound).
    this._visibleConversationId = conversationId
    void this.markAsRead(conversationId)
    await this.joinConversation(conversationId)
  }

  unregisterRoom(conversationId: string): void {
    this.rooms.delete(conversationId)
    if (this._visibleConversationId === conversationId) this._visibleConversationId = null
    void this.leaveConversation(conversationId)
  }

  roomIds(): string[] {
    return [...this.rooms.keys()]
  }

  isJoined(conversationId: string): boolean {
    return this.joined.has(conversationId)
  }

  async connect(): Promise<void> {
    if (this.connected) {
      this.startHeartbeat()
      return
    }
    try {
      await this.hub.connect()
      this.connected = true
      this.error = null
      for (const conversationId of this.rooms.keys()) {
        await this.joinConversation(conversationId)
      }
      this.startHeartbeat()
    } catch (err) {
      this.connected = false
      this.report(err)
    }
    this.emitConnection(this.connected)
  }

  async disconnect(): Promise<void> {
    this.stopHeartbeat()
    await this.hub.disconnect()
    if (this.connected) {
      this.connected = false
      this.emitConnection(false)
    }
  }

  /** Mobile `catchUp`: replay missed events after (re)connect. */
  async catchUp(): Promise<void> {
    try {
      const res = await api.get('/chat/events', {
        params: { after: await getSyncCursor(), limit: CATCH_UP_LIMIT },
      })
      if (!Array.isArray(res.data)) return
      for (const item of res.data) {
        if (item != null && typeof item === 'object') {
          await this.applyEvent(item as Record<string, unknown>)
        }
      }
    } catch (err) {
      this.report(err)
    }
  }

  /** Serialized reduction: events apply in arrival order, never concurrently. */
  enqueueEvent(event: Record<string, unknown>): void {
    this.queue = this.queue.then(() => this.applyEvent(event))
  }

  private async applyEvent(event: Record<string, unknown>): Promise<void> {
    const parsed = parseChatEvent(event)
    if (!parsed) {
      this.report(new Error('Malformed ChatEvent: message required'))
      return
    }
    const message = parseMessage(parsed.message)
    if (!message) {
      this.report(new Error('Malformed ChatEvent message'))
      return
    }
    if (parsed.eventId) {
      // Only `message.sent` carries a persistable row (mobile `_messageToRow` gating).
      const persisted = await applyEvent(
        parsed.eventId,
        parsed.cursor,
        parsed.eventType === 'message.sent' ? messageToRow(message) : undefined,
      )
      if (!persisted) return
    }
    if (parsed.eventType === 'message.sent') {
      const recipient = message.senderId !== this.userId
      const visible = this._visibleConversationId === message.conversationId
      this.rooms.get(message.conversationId)?.onMessage?.(message)
      const inbound = { message, recipient, visible }
      this.events.onInbound?.(inbound)
      for (const sub of this.subscriptions) sub.onInbound?.(inbound)
      if (visible && recipient) {
        // The open room already shows it: confirm read server-side (mobile does both).
        void this.markAsRead(message.conversationId)
      }
      if (recipient) {
        // Delivery receipt so the sender's bubble advances (errors only reported).
        void api
          .post(`/chat/messages/${message.id}/delivered`, { deviceId: getChatDeviceId() })
          .catch((err: unknown) => this.report(err))
      }
      return
    }
    const status = parsed.eventType.split('.').pop() as MessageStatus
    if (LIFECYCLE_STATUSES.includes(status)) {
      // Mobile broadcasts lifecycle to every room; rooms ignore unknown ids.
      for (const room of this.rooms.values()) room.onLifecycle?.(message.id, status)
    }
  }

  private async joinConversation(conversationId: string): Promise<void> {
    if (!this.connected || !this.rooms.has(conversationId)) return
    try {
      await this.hub.joinConversation(conversationId)
      this.joined.add(conversationId)
      this.error = null
    } catch (err) {
      this.joined.delete(conversationId)
      this.report(err)
      throw err
    }
  }

  private async leaveConversation(conversationId: string): Promise<void> {
    this.joined.delete(conversationId)
    if (!this.connected) return
    try {
      await this.hub.leaveConversation(conversationId)
    } catch {
      // Leaving is best-effort (mobile swallows the same way).
    }
  }

  private wire(): void {
    // Mobile `_wireEvents`: the session owns reduction; reconnects rejoin groups.
    this.hub.setEvents({
      onChatEvent: (event) => this.enqueueEvent(event),
      onReconnected: () => {
        void (async () => {
          this.joined.clear()
          for (const conversationId of this.rooms.keys()) {
            await this.joinConversation(conversationId)
          }
          this.startHeartbeat()
          await this.catchUp()
          this.emitConnection(true)
        })()
      },
      onError: (error) => this.report(error),
      onTyping: (conversationId, userId) => this.showTyping(conversationId, userId),
      onMessagesRead: (conversationId, userId) => {
        this.rooms.get(conversationId)?.onRead?.(userId)
      },
      onUserOnline: (userId) => this.setOnline(userId, true),
      onUserOffline: (userId) => this.setOnline(userId, false),
    })
  }

  private setOnline(userId: string, online: boolean): void {
    if (this.onlineUsers.get(userId) === online) return
    this.onlineUsers.set(userId, online)
    this.events.onPresenceChange?.(userId, online)
    for (const sub of this.subscriptions) sub.onPresenceChange?.(userId, online)
  }

  /** Seed presence from the cached list (mobile `startSession` seeding). */
  seedPresence(entries: Array<{ userId: string; online: boolean }>): void {
    for (const { userId, online } of entries) {
      if (!this.onlineUsers.has(userId)) this.onlineUsers.set(userId, online)
    }
  }

  /**
   * Mobile `onUserTyping`: ignore our own echo, route to the open room,
   * auto-clear after 3s while the same room stays registered.
   */
  private showTyping(conversationId: string, userId: string): void {
    if (!userId || userId === this.userId) return
    const room = this.rooms.get(conversationId)
    if (!room) return
    room.onTyping?.(userId)
    this.typingTimers.get(conversationId)?.()
    this.typingTimers.set(
      conversationId,
      this.schedule(TYPING_EXPIRY_MS, () => {
        this.typingTimers.delete(conversationId)
        if (this.rooms.get(conversationId) === room) room.onTyping?.(null)
      }),
    )
  }

  /**
   * Mobile `sendTyping`: joined rooms only, throttled to one indicator
   * per second per conversation.
   */
  async sendTyping(conversationId: string): Promise<void> {
    if (!this.joined.has(conversationId)) return
    const now = Date.now()
    const previous = this.lastTyping.get(conversationId)
    if (previous != null && now - previous < TYPING_THROTTLE_MS) return
    this.lastTyping.set(conversationId, now)
    try {
      await this.hub.sendTyping(conversationId)
    } catch (err) {
      this.report(err)
    }
  }

  /** Mobile `markAsRead`: REST confirm, failures swallowed (the UI already updated). */
  async markAsRead(conversationId: string): Promise<void> {
    try {
      await api.post(`/conversations/${conversationId}/read`)
    } catch {
      // Silent like mobile.
    }
  }

  /**
   * Mobile `didChangeAppLifecycleState`: resume reconnects + catches up,
   * background only parks the heartbeat (the socket stays).
   */
  observeLifecycle(): () => void {
    const onVisibility = () => {
      if (document.visibilityState === 'visible' && this.userId != null) {
        void this.connect().then(() => this.catchUp())
      } else if (document.visibilityState === 'hidden') {
        this.stopHeartbeat()
      }
    }
    const onOnline = () => {
      if (this.userId != null) void this.connect().then(() => this.catchUp())
    }
    document.addEventListener('visibilitychange', onVisibility)
    window.addEventListener('online', onOnline)
    return () => {
      document.removeEventListener('visibilitychange', onVisibility)
      window.removeEventListener('online', onOnline)
    }
  }

  private startHeartbeat(): void {
    this.stopHeartbeat()
    if (!this.connected) return
    this.stopHeartbeatFn = this.timer(HEARTBEAT_INTERVAL_MS, async () => {
      try {
        await this.hub.heartbeat()
      } catch {
        await this.disconnect()
        await this.connect()
        await this.catchUp()
      }
    })
  }

  private stopHeartbeat(): void {
    this.stopHeartbeatFn?.()
    this.stopHeartbeatFn = null
  }

  private emitConnection(connected: boolean): void {
    this.events.onConnectionChange?.(connected)
    for (const sub of this.subscriptions) sub.onConnectionChange?.(connected)
  }

  private report(error: unknown): void {
    this.error = error
    this.events.onError?.(error)
    for (const sub of this.subscriptions) sub.onError?.(error)
  }
}
